import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { Flame, Trophy, Sparkles } from 'lucide-react';

export default function WeeklyChallenges() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/challenges`);
      setData(res.data);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleClaim = async (challengeId) => {
    try {
      const res = await axios.post(`${API}/challenges/${challengeId}/claim`);
      if (res.data.ok) {
        toast.success(`🎉 +${res.data.reward} points — ${res.data.title}!`, {
          description: res.data.all_complete ? `Streak: ${res.data.streak} week${res.data.streak > 1 ? 's' : ''}!` : undefined
        });
        load();
      } else {
        toast.error(res.data.error || 'Could not claim');
      }
    } catch (err) {
      toast.error('Failed to claim reward');
    }
  };

  if (loading) return null;
  if (!data) return null;

  const daysLeft = Math.max(0, Math.ceil((new Date(data.week_ends_at) - new Date()) / (1000 * 60 * 60 * 24)));

  return (
    <Card data-testid="weekly-challenges" className="glass p-5 border border-purple-500/40 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-400" />
          <h3 className="font-bold text-white">Weekly Challenges</h3>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          {data.streak > 0 && (
            <span className="flex items-center gap-1 text-orange-400">
              <Flame className="w-3 h-3" /> {data.streak}wk streak
            </span>
          )}
          <span>{daysLeft}d left</span>
        </div>
      </div>

      <div className="space-y-3">
        {data.challenges.map(ch => (
          <div key={ch.id} data-testid={`challenge-${ch.id}`}
            className="p-3 bg-black/40 rounded border border-slate-700">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-xl">{ch.icon}</span>
                <div>
                  <p className="text-sm font-bold text-white">{ch.title}</p>
                  <p className="text-xs text-zinc-400">{ch.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-mono text-yellow-400">+{ch.reward}</p>
                <p className="text-xs text-zinc-500">pts</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Progress value={ch.percent} className="h-1.5 flex-1" />
              <span className="text-xs text-zinc-400 w-14 text-right">{ch.progress}/{ch.goal}</span>
            </div>
            {ch.completed && !ch.claimed && (
              <Button data-testid={`claim-${ch.id}`}
                onClick={() => handleClaim(ch.id)}
                className="mt-2 w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-black hover:opacity-90"
                size="sm">
                <Sparkles className="w-3 h-3 mr-1" /> Claim {ch.reward} Points
              </Button>
            )}
            {ch.claimed && (
              <p className="mt-1 text-xs text-green-400">✓ Claimed</p>
            )}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-zinc-500 mt-3 text-center">
        Challenges refresh every Monday · Complete all 3 to build your streak
      </p>
    </Card>
  );
}
